/**
 * PermissionGate Component
 * 
 * Conditionally renders children based on user permissions.
 * Gracefully hides content when permission is not granted.
 * 
 * Usage:
 * ```tsx
 * <PermissionGate permission={PERMISSIONS.MANAGE_USERS}>
 *   <Button>Invite User</Button>
 * </PermissionGate>
 * 
 * <PermissionGate anyPermissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.INVITE_USERS]}>
 *   <Button>Invite</Button>
 * </PermissionGate>
 * 
 * <PermissionGate permission={PERMISSIONS.VIEW_USERS} fallback={<p>No access</p>}>
 *   <UserList />
 * </PermissionGate>
 * ```
 */

import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';

export interface PermissionGateProps {
  /**
   * Single permission required
   */
  permission?: string;
  
  /**
   * Array of permissions (user needs ANY one)
   */
  anyPermissions?: string[];
  
  /**
   * Array of permissions (user needs ALL)
   */
  allPermissions?: string[];
  
  /**
   * Content to show when permission is denied
   * Defaults to null (hides completely)
   */
  fallback?: React.ReactNode;
  
  /**
   * Content to render when permission is granted
   */
  children: React.ReactNode;
}

export function PermissionGate({
  permission,
  anyPermissions,
  allPermissions,
  fallback = null,
  children,
}: PermissionGateProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // During loading, hide content to prevent flicker
  if (isLoading) {
    return null;
  }

  let hasAccess = false;

  // Check permission based on provided props
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyPermissions) {
    hasAccess = hasAnyPermission(anyPermissions);
  } else if (allPermissions) {
    hasAccess = hasAllPermissions(allPermissions);
  } else {
    // No permission specified - deny by default
    hasAccess = false;
  }

  // Show fallback or hide if no access
  if (!hasAccess) {
    return <>{fallback}</>;
  }

  // User has permission - render children
  return <>{children}</>;
}

